<template>
  <b-row>
    <b-col sm="12">
      <b-card header-tag="header" footer-tag="footer">
        <b-row>
          <b-col sm="7">
            <h4 id="traffic" class="card-title mb-0">Movimientos</h4>
            <div class="small text-muted">Consultar movimientos contables.</div>
          </b-col>
          <b-col sm="5">
            <b-button variant="primary" size="sm" class="float-right" @click="openManualAccounting()">
              <b-icon icon="plus-circle" size="sm" class="mr-1" style="font-size:15px;"></b-icon>
              Nuevo asiento manual
            </b-button>
          </b-col>
        </b-row>
      </b-card>

      <b-card header-tag="header" footer-tag="footer">
        <b-row v-if="table.mostrarFiltros" class="align-items-end">
          <b-col md="2">
            <label class="filter-label">Referencia</label>
            <b-form-input type="text" size="sm" v-model="filters.reference" placeholder="Buscar..." @keyup.enter="filterReportReset()"></b-form-input>
          </b-col>
          <b-col md="2">
            <label class="filter-label">Empresa</label>
            <b-form-select size="sm" v-model="filters.business_id" :options="arr.businesses" @change="onBusinessChange()"></b-form-select>
          </b-col>
          <b-col md="1">
            <label class="filter-label">Tipo</label>
            <b-form-select size="sm" v-model="filters.declared" :options="arr.select.declared" @change="onFilterDeclaredChange()"></b-form-select>
          </b-col>
          <b-col md="1">
            <label class="filter-label">Desde</label>
            <b-form-input type="date" size="sm" v-model="filters.date_start"></b-form-input>
          </b-col>
          <b-col md="1">
            <label class="filter-label">Hasta</label>
            <b-form-input type="date" size="sm" v-model="filters.date_end"></b-form-input>
          </b-col>
          <b-col md="1">
            <label class="filter-label">Estado</label>
            <b-form-select size="sm" v-model="filters.status" :options="arr.select.status"></b-form-select>
          </b-col>
          <b-col md="1">
            <label class="filter-label">Origen</label>
            <b-form-select size="sm" v-model="filters.origin" :options="arr.select.origin"></b-form-select>
          </b-col>
          <b-col md="2">
            <label class="filter-label">Cuenta contable</label>
            <b-form-select size="sm" v-model="filters.accounting_accounts_id" :options="arr.leafAccounts"></b-form-select>
          </b-col>
          <b-col cols="auto" class="d-flex align-items-end">
            <b-button variant="outline-dark" size="sm" @click="filterReportReset()" v-b-tooltip.hover title="Filtrar" style="height:31px; width:31px; padding:0; flex-shrink:0;">
              <b-icon icon="filter" style="font-size:15px;"></b-icon>
            </b-button>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col>
            <b-table class="mb-0"
                    responsive="sm"
                    head-variant="dark"
                    :hover="true"
                    :small="true"
                    :fixed="false"
                    :fields="table.fields"
                    :filter="table.filter"
                    :current-page="table.currentPage"
                    :per-page="table.perPage"
                    :items="table.items"
                    v-if="table.items.length">

              <template v-slot:cell(date)="data">
                <div v-if="data.item.date">
                  {{ moment(data.item.date).format('DD MMMM YYYY') }}
                </div>
              </template>

              <template v-slot:cell(nd_marker)="data">
                <span v-if="data.item.declared != null && !data.item.declared" class="nd-marker" v-b-tooltip.hover title="ND"></span>
              </template>

              <template v-slot:cell(reference)="data">
                {{data.item.reference}}
              </template>

              <template v-slot:cell(observations)="data">
                {{data.item.observations}}
              </template>

              <template v-slot:cell(origin)="data">
                <b-badge :variant="originVariant(data.item.origin)" style="font-size:12px; padding:4px 8px;">
                  {{ originLabel(data.item.origin) }}
                </b-badge>
              </template>

              <template v-slot:cell(total_debit)="data">
                <b>{{ formatCurrency(data.item.total_debit) }}</b>
              </template>

              <template v-slot:cell(total_credit)="data">
                <b>{{ formatCurrency(data.item.total_credit) }}</b>
              </template>

              <template v-slot:cell(difference)="data">
                <b-badge :variant="data.item.is_balanced ? 'success' : 'danger'" style="font-size:12px; padding:4px 8px;">
                  {{ formatCurrency(data.item.difference) }}
                </b-badge>
              </template>

              <template v-slot:cell(status)="data">
                <b-badge :variant="data.item.is_balanced ? 'success' : 'danger'" style="font-size:12px; padding:4px 8px;">
                  {{ data.item.is_balanced ? 'Balanceado' : 'Con diferencia' }}
                </b-badge>
              </template>

              <template v-slot:cell(f_action)="data">
                <b-button size="sm" variant="outline-dark" class="mr-1" @click="openDetail(data.item)" v-b-tooltip.hover title="Ver detalle">
                  <b-icon icon="eye" aria-hidden="true"></b-icon>
                </b-button>
                <b-button size="sm" variant="outline-primary" class="mr-1" @click="openManualAccounting(data.item)" v-if="data.item.origin == 'manual' && !isPeriodLocked(data.item.date)" v-b-tooltip.hover title="Editar asiento manual">
                  <b-icon icon="pencil" aria-hidden="true"></b-icon>
                </b-button>
                <b-button size="sm" variant="outline-danger" @click="deleteManualAccounting(data.item)" v-if="data.item.origin == 'manual' && !isPeriodLocked(data.item.date)" v-b-tooltip.hover title="Eliminar asiento manual">
                  <b-icon icon="trash" aria-hidden="true"></b-icon>
                </b-button>
              </template>
            </b-table>
            <b-alert v-else variant="warning" show>No se encontraron registros</b-alert>
          </b-col>
        </b-row>
      </b-card>

      <b-card header-tag="header" footer-tag="footer">
        <b-row>
          <b-col>
            <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
              <i class="fa fa-angle-double-left"></i>
              Volver
            </b-button>
          </b-col>

          <b-col>
            <nav v-if="table.tablePaginate">
              <b-pagination class="pull-right mb-0"
                              size="sm"
                              pills=""
                              :total-rows="table.tablePaginate.total"
                              :per-page="table.tablePaginate.per_page"
                              v-model="table.currentPage"
                              @input="filterReport()" />
            </nav>
          </b-col>
        </b-row>
      </b-card>

      <b-modal v-model="modal.manual.active"
               header-bg-variant="dark"
               header-text-variant="white"
               size="xl"
               :key="modal.manual._id"
               :id="modal.manual._id">
        <div slot="modal-header" minimize>
          {{ modal.manual.title }}
        </div>

        <b-row>
          <b-col md="2">
            <b-form-group label="Empresa *">
              <b-form-select size="sm" v-model="crud.manual.business_id" :options="arr.businesses"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group label="Tipo *">
              <b-form-select size="sm" v-model="crud.manual.declared" :options="arr.select.declaredRequired"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group label="Fecha">
              <b-form-input type="date" size="sm" v-model="crud.manual.date"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Referencia">
              <b-form-input type="text" size="sm" v-model="crud.manual.reference" placeholder="Ej: AJUSTE-0001"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Observaciones">
              <app-textarea size="sm" v-model="crud.manual.observations" rows="2" max-rows="6"></app-textarea>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <AccountingEntryBuilder
              :key="'manual_accounting_' + manualAccountingKey"
              typeDebe=""
              typeHaber=""
              :defaultDebe="crud.manual.defaultDebe"
              :defaultHaber="crud.manual.defaultHaber"
              :reference="crud.manual.reference"
              :amountTotal="0"
              validationMode="manual"
              :showConstructor="true"
              @getAsiento="loadManualAccountingDetail($event)" />
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.manual.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="saveManualAccounting()">Guardar</b-button>
        </div>
      </b-modal>

      <b-modal v-model="modal.detail.active"
               header-bg-variant="dark"
               header-text-variant="white"
               size="lg"
               :key="modal.detail._id"
               :id="modal.detail._id">
        <div slot="modal-header" minimize>
          {{ modal.detail.title }}
        </div>

        <div v-if="selectedAccounting">
          <div class="d-flex flex-wrap mb-3 px-3 py-2 bg-light rounded border">
            <div class="mr-4">
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Fecha</small>
              <span>{{ moment(selectedAccounting.date).format('DD/MM/YYYY') }}</span>
            </div>
            <div class="mr-4">
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Referencia</small>
              <span>{{ selectedAccounting.reference }}</span>
            </div>
            <div class="mr-4">
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Origen</small>
              <b-badge :variant="selectedAccounting.origin == 'manual' ? 'info' : 'secondary'" style="font-size:12px; padding:3px 8px;">
                {{ selectedAccounting.origin_movement ? selectedAccounting.origin_movement.label : selectedAccounting.origin }}
              </b-badge>
            </div>
            <div>
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Estado</small>
              <b-badge :variant="selectedAccounting.is_balanced ? 'success' : 'danger'" style="font-size:12px; padding:3px 8px;">
                {{ selectedAccounting.is_balanced ? 'Balanceado' : 'Con diferencia' }}
              </b-badge>
            </div>
          </div>

          <b-table class="mb-0"
                   responsive="sm"
                   head-variant="dark"
                   :hover="true"
                   :small="true"
                   :items="tableSub.items"
                   :fields="tableSub.fields"
                   :tbody-tr-class="subTableRowClass"
                   v-if="tableSub.items.length">
            <template v-slot:cell(code)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{data.item.accounting_accounts_code}}
              </span>
            </template>
            <template v-slot:cell(name)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{data.item.accounting_accounts_name}}
              </span>
            </template>
            <template v-slot:cell(debit)="data">
              <b v-if="data.item.type == 'Debe'" class="accounting-entries-amount">{{ formatCurrency(data.item.total) }}</b>
            </template>
            <template v-slot:cell(credit)="data">
              <b v-if="data.item.type == 'Haber'" class="accounting-entries-amount">{{ formatCurrency(data.item.total) }}</b>
            </template>
          </b-table>
          <b-alert v-else variant="warning" show>No se encontraron registros</b-alert>

          <div class="d-flex justify-content-end mt-3">
            <div class="text-center border rounded px-3 py-2 mr-2" style="min-width:150px; background:#fff3cd;">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Debe</small>
              <strong style="font-size:15px;">{{ formatCurrency(selectedAccounting.total_debit) }}</strong>
            </div>
            <div class="text-center border rounded px-3 py-2 mr-2" style="min-width:150px; background:#d1ecf1;">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Haber</small>
              <strong style="font-size:15px;">{{ formatCurrency(selectedAccounting.total_credit) }}</strong>
            </div>
            <div class="text-center border rounded px-3 py-2" style="min-width:150px;"
                 :style="{ background: selectedAccounting.is_balanced ? '#d4edda' : '#f8d7da' }">
              <small class="d-block font-weight-bold text-uppercase" style="font-size:10px;"
                     :class="selectedAccounting.is_balanced ? 'text-success' : 'text-danger'">Diferencia</small>
              <strong style="font-size:15px;"
                      :class="selectedAccounting.is_balanced ? 'text-success' : 'text-danger'">{{ formatCurrency(selectedAccounting.difference) }}</strong>
            </div>
          </div>
        </div>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" @click="modal.detail.active=false">Cerrar</b-button>
        </div>
      </b-modal>
    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import moment from 'moment'
  import FindObject from '@/components/inc/find/findObject'
  import AccountingEntryBuilder from '@/components/inc/accounting/entries'

  export default {
    components: {
      FindObject,
      AccountingEntryBuilder,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.CONTABILIDAD,
          profile_id: Profiles.PERSONAL,
          view_reference: 'accountingEntries',
          elements: {}
        },
        table : {
          items: [],
          filters: null,
          fields: [
            {key: 'date', label: 'Fecha', class: "align-middle"},
            {key: 'nd_marker', label: '', class: "align-middle text-center p-0", thStyle: 'width:24px'},
            {key: 'reference', label: 'Referencia', class: "align-middle"},
            {key: 'observations', label: 'Observaciones', class: "align-middle"},
            {key: 'origin', label: 'Origen', class: "align-middle text-center"},
            {key: 'total_debit', label: 'Debe', class: "align-middle text-right"},
            {key: 'total_credit', label: 'Haber', class: "align-middle text-right"},
            {key: 'difference', label: 'Diferencia', class: "align-middle text-right"},
            {key: 'status', label: 'Estado', class: "align-middle text-center"},
            {key: 'f_action', label:'', class: "align-middle"},
          ],
          mostrarFiltros: true,
          currentPage: 1,
          perPage: 0,
          tablePaginate: null,
        },
        tableSub : {
          items: [],
          fields: [
            {key: 'code', label: 'Código', class:'align-middle'},
            {key: 'name', label: 'Nombre', class:'align-middle'},
            {key: 'debit', label: 'Debe', class:'align-middle text-right'},
            {key: 'credit', label: 'Haber', class:'align-middle text-right'},
          ],
          filter: null,
          mostrarFiltros: false,
          currentPage: 1,
          perPage: 50,
        },
        arr: {
          accounting: [],
          businesses: [],
          leafAccounts: [],
          select: {
            status: [
              { value: '', text: 'Todos' },
              { value: 'balanced', text: 'Balanceado' },
              { value: 'unbalanced', text: 'Con dif.' },
            ],
            origin: [
              { value: '', text: 'Todos' },
              { value: 'automatic', text: 'Auto' },
              { value: 'manual', text: 'Manual' },
              { value: 'closing', text: 'Cierre' },
              { value: 'opening', text: 'Apertura' },
            ],
            declared: [
              { value: '', text: 'Todos' },
              { value: '1', text: 'D' },
              { value: '0', text: 'ND' },
            ],
            declaredRequired: [
              { value: null, text: 'Seleccionar tipo...' },
              { value: true, text: 'Declarado (D)' },
              { value: false, text: 'No Declarado (ND)' },
            ],
          }
        },
        filters: {
          date_start: '',
          date_end: '',
          reference: '',
          accounting_accounts_id: 0,
          status: '',
          origin: '',
          business_id: 0,
          declared: '',
        },
        crud: {
          manual: {
            id: 0,
            date: '',
            reference: '',
            observations: '',
            origin: 'manual',
            detail: null,
            defaultDebe: [],
            defaultHaber: [],
            business_id: 0,
            business_obj: null,
            declared: null,
          }
        },
        modal: {
          manual: {
            active: false,
            title: 'Nuevo asiento manual',
            _id: '',
          },
          detail: {
            active: false,
            title: 'Detalle de asiento',
            _id: '',
          }
        },
        manualAccountingKey: 0,
        selectedAccounting: null,
        maxClosingDate: null,
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    },
    mounted() {
      this.filterLoad()
      this.loadBusinesses()
      this.loadLeafAccounts()
    },
    computed: {
      getCurrency() {
        return 'ARS'
      },
    },
    methods: {
      moment,
      loadBusinesses() {
        serviceAPI.obtenerEmpresas()
        .then(response => {
          const businesses = response.data || []
          this.arr.businesses = businesses.map(b => ({ value: b.id, text: b.name }))
          if(businesses.length) {
            this.filters.business_id = businesses[0].id
          }
          this.loadClosingDates()
          this.filterReport()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      loadLeafAccounts() {
        serviceAPI.filtrarPlanCuenta()
        .then(response => {
          const leaves = []
          this.collectLeafAccounts(response.data || [], leaves)
          this.arr.leafAccounts = [
            { value: 0, text: 'Todas las cuentas' },
            ...leaves.map(a => ({ value: a.id, text: a.code + ' - ' + a.name }))
          ]
        })
      },
      collectLeafAccounts(accounts, result) {
        accounts.forEach(account => {
          const children = account.children_recurrent || account.children || []
          if(children.length) {
            this.collectLeafAccounts(children, result)
          } else {
            result.push(account)
          }
        })
      },
      loadClosingDates() {
        const params = {}
        if(this.filters.business_id) params.business_id = this.filters.business_id
        if(this.filters.declared !== '') params.declared = this.filters.declared

        serviceAPI.obtenerFechasCierre(params)
        .then(response => {
          const dates = response.data || []
          this.maxClosingDate = dates.length ? dates[0] : null
        })
      },
      onBusinessChange() {
        this.loadClosingDates()
        this.filterReportReset()
      },
      onFilterDeclaredChange() {
        this.loadClosingDates()
      },
      loadManualBusiness(object) {
        if(object) {
          this.crud.manual.business_obj = object
          this.crud.manual.business_id = object.id
        } else {
          this.crud.manual.business_obj = null
          this.crud.manual.business_id = 0
        }
      },
      isPeriodLocked(date) {
        if(!this.maxClosingDate || !date) return false
        return date.substring(0, 10) <= this.maxClosingDate
      },
      subTableRowClass(item) {
        if (!item) return ''
        return item.type === 'Debe' ? 'table-warning' : 'table-info'
      },
      originVariant(origin) {
        const map = { manual: 'info', closing: 'dark', opening: 'success' }
        return map[origin] || 'secondary'
      },
      originLabel(origin) {
        const map = { manual: 'Manual', closing: 'Cierre', opening: 'Apertura' }
        return map[origin] || 'Automático'
      },
      openDetail(item) {
        this.selectedAccounting = item
        this.tableSub.items = item.details || []
        this.modal.detail._id = Math.random().toString(36).slice(2, 8)
        this.modal.detail.title = 'Detalle de asiento: ' + item.reference
        this.modal.detail.active = true
      },
      filterLoad() {
        this.filters.date_start = moment().format('YYYY-MM-DD')
        this.filters.date_end = moment().format('YYYY-MM-DD')
      },
      filterReportReset() {
        this.table.currentPage = 1
        this.table.rowSelected = null
        this.tableSub.items = []
        this.filterReport()
      },
      clearFilters() {
        this.table.currentPage = 1
        this.table.rowSelected = null
        this.tableSub.items = []
        this.filterLoad()
        this.filters.reference = ''
        this.filters.accounting_accounts_id = 0
        this.filters.status = ''
        this.filters.origin = ''
        this.filters.business_id = this.arr.businesses.length ? this.arr.businesses[0].value : 0
        this.filters.declared = ''
        this.filterReport()
      },
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR',{style:'currency',currency: this.getCurrency}).format(value || 0)
      },
      openManualAccounting(item) {
        this.crud.manual.id = item ? item.id : 0
        this.crud.manual.date = item ? item.date : moment().format('YYYY-MM-DD')
        this.crud.manual.reference = item ? item.reference : ''
        this.crud.manual.observations = item ? item.observations : ''
        this.crud.manual.origin = 'manual'
        this.crud.manual.detail = null
        this.crud.manual.defaultDebe = []
        this.crud.manual.defaultHaber = []
        this.crud.manual.business_id = item ? (item.business_id || 0) : (this.arr.businesses.length ? this.arr.businesses[0].value : 0)
        this.crud.manual.business_obj = null
        this.crud.manual.declared = item ? (item.declared !== null && item.declared !== undefined ? item.declared : null) : null

        if(item && item.details) {
          item.details.forEach(element => {
            let row = {
              account: {
                id: element.accounting_accounts_id,
                code: element.accounting_accounts_code,
                name: element.accounting_accounts_name,
              },
              amount: parseFloat(element.total),
            }

            if(element.type == 'Debe') {
              this.crud.manual.defaultDebe.push(row)
            }

            if(element.type == 'Haber') {
              this.crud.manual.defaultHaber.push(row)
            }
          })
        }

        this.manualAccountingKey = this.manualAccountingKey + 1

        this.modal.manual._id = Math.random().toString(36).slice(2, 8)
        this.modal.manual.title = item ? 'Editar asiento manual' : 'Nuevo asiento manual'
        this.modal.manual.active = true
      },
      loadManualAccountingDetail(object) {
        this.crud.manual.detail = object
      },
      saveManualAccounting() {
        if(!this.crud.manual.business_id) {
          this.$awn.alert('Debe seleccionar la empresa')
          return false
        }

        if(this.crud.manual.declared === null || this.crud.manual.declared === undefined) {
          this.$awn.alert('Debe seleccionar el tipo (Declarado / No Declarado)')
          return false
        }

        if(!this.crud.manual.date) {
          this.$awn.alert('No se cargó la fecha')
          return false
        }

        if(!this.crud.manual.reference) {
          this.$awn.alert('No se cargó la referencia')
          return false
        }

        if(!this.crud.manual.detail) {
          this.$awn.alert('Revise el asiento contable')
          return false
        }

        let loader = this.$loading.show()
        let result = this.crud.manual.id ? serviceAPI.editarAsiento(this.crud.manual) : serviceAPI.agregarAsiento(this.crud.manual)

        result
        .then(() => {
          loader.hide()
          this.modal.manual.active = false
          this.filterReportReset()
          this.$awn.success('Asiento manual guardado con éxito')
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      deleteManualAccounting(item) {
        this.$bvModal.msgBoxConfirm("¿Desea eliminar el asiento manual '"+ item.reference +"'?", {
          title: 'Eliminar asiento manual',
          size: 'md',
          buttonSize: 'md',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          headerBgVariant: 'danger',
          headerTextVariant: 'white',
          hideHeaderClose: false,
          centered: true
        })
        .then(value => {
          if(value) {
            let loader = this.$loading.show()
            serviceAPI.eliminarAsiento(item.id)
            .then(() => {
              loader.hide()
              this.filterReportReset()
              this.$awn.success('Asiento manual eliminado')
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            })
          }
        })
      },
      filterReport(){
        let loader = this.$loading.show()

        var result = serviceAPI.filtrarAsientos(this.filters, this.table.currentPage)

        result.then((response) => {
          loader.hide()
          this.table.items = response.data.data || []
          this.table.tablePaginate = response.data

          this.arr.accounting = response.data.data || []

          this.tableSub.items = []
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        });
      },
    }
  }
</script>
<style scoped>
  .filter-find-object ::v-deep fieldset {
    margin-bottom: 0 !important;
  }

  .accounting-entries-amount {
    font-size: 13px;
  }

  .filter-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 2px;
    color: #495057;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .nd-marker {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #fd7e14;
    cursor: default;
  }
</style>
